import i18next from 'i18next';

import { COMMON_WEB_PATH_JP } from '../constant';
import { REPLACE_AIR } from '../replace_str_constant';

const BASE_DIR = 'game-icon-ds88';

/* 每一組內的副檔名嘗試順序,見下方 buildGameImageCandidates 的說明。
   猜錯只是多一次 404,要調整就改這兩行。 */
const LONG_EXTS = ['webp', 'gif'];
const SQUARE_EXTS = ['gif', 'webp'];

/**
 * 遊戲圖片來源解析。
 *
 * 為什麼要抽成 function:
 * 原本同一套規則散在三個地方 —— GameItem 的 preload probe、<LazyImage src> 的
 * 三元式、以及 onError 裡一串 endsWith 比對。三份各寫各的,結果就是長圖那條
 * 只有 probe 有、src 有、onError 完全沒有,長圖一 404 就直接死在畫面上。
 * 現在只有這裡一份候選清單,三邊都吃它。
 *
 * ── 檔名 ──
 * 用 REPLACE_AIR(把空白**刪掉**),與傳統站既有資產一致。
 * 注意 cli-integrated-api 走的是 REPLACE_SPACE(空白換底線 `_`),兩邊本來就不同;
 * 現有圖片都是靠刪空白版命名載到的,所以這裡刻意不跟 SDK 靠齊。
 * 之後若 S3 統一成底線版,只要改這一行。
 *
 * ── 語系 ──
 * 長圖與方圖都先走 `{lang}/`,失敗才退 `en/`。
 * 圖上通常有在地化文字,所以**語系優先於格式** —— 寧可拿同語系的另一種副檔名,
 * 也不要太早掉到英文版。
 *
 * ── fallback 順序(以 lang=jp、要長圖為例)──
 *   1. jp/_long.webp   2. jp/_long.gif
 *   3. en/_long.webp   4. en/_long.gif
 *   5. jp/.gif         6. jp/.webp
 *   7. en/.gif         8. en/.webp
 * 長圖整組都缺才退回方圖 —— 版位會比較空,但總比破圖好。
 *
 * 每組內的副檔名順序刻意不同:
 *   長圖 webp 先 —— 原本的程式只試過 `_long.webp`,代表這批資產以 webp 為主。
 *   方圖 gif 先 —— 沿用既有行為,有動畫的優先。
 * 這只是「猜中率」的問題,猜錯就多一次 404 而已;若日後發現長圖多半是 gif,
 * 把下面 LONG_EXTS 的順序對調即可。
 *
 * @param {object}  p
 * @param {string}  p.platform   遊戲商代碼,例如 "WM"
 * @param {string}  p.gameCode   後端給的 gameCode(可能含空白)
 * @param {boolean} p.longImage  是否優先取長圖
 * @param {string}  [p.lang]     語系,預設取 i18next.language
 * @returns {string[]} 由優先到備援的完整 URL 清單
 */
export const buildGameImageCandidates = ({
  platform,
  gameCode,
  longImage = false,
  lang = i18next.language
} = {}) => {
  const code = REPLACE_AIR(gameCode);
  if (!platform || !code) return [];

  const base = `${COMMON_WEB_PATH_JP}/${BASE_DIR}`;
  const at = (folder, suffix, ext) =>
    `${base}/${folder}/${platform}/${code}${suffix}.${ext}`;

  /* lang 可能就是 en,或是拿不到值(i18next 還沒 init)。
     兩種情況下語系那組會跟 en 那組完全一樣,去重免得白白多打一次 404。 */
  const folders = lang && lang !== 'en' ? [lang, 'en'] : ['en'];

  /* 每個語系資料夾內把該種類的副檔名都試過,再換下一個語系 */
  const group = (suffix, exts) =>
    folders.flatMap((folder) => exts.map((ext) => at(folder, suffix, ext)));

  const square = group('', SQUARE_EXTS);
  if (!longImage) return square;

  return [...group('_long', LONG_EXTS), ...square];
};

/**
 * 給 <img onError> 用:回傳下一個該試的 URL,沒有了就回 null。
 *
 * 用「目前 src 在清單中的位置 + 1」來推進,而不是比對副檔名或路徑尾巴。
 * 舊寫法是一串 endsWith,新增一種路徑(例如 _long)就得再補一個 else if,
 * 漏補就整條斷掉 —— 長圖沒有 fallback 正是這樣來的。
 *
 * img.src 讀出來一定是絕對路徑,候選本來就是絕對路徑,可以直接比對;
 * 保險起見找不到時當作「還沒開始」,從第二個候選接下去。
 *
 * @param {string}   currentSrc  e.target.src
 * @param {string[]} candidates  buildGameImageCandidates() 的結果
 * @returns {string|null}
 */
export const nextGameImageSrc = (currentSrc, candidates = []) => {
  if (!candidates.length) return null;
  const idx = candidates.indexOf(currentSrc);
  const next = idx === -1 ? 1 : idx + 1;
  return next < candidates.length ? candidates[next] : null;
};

/* 探測結果快取:url → true(載得到) / false(壞的)。
   長圖加上語系維度之後候選最多到 8 段,最壞情況每張卡片要打 8 次 404。
   Splide 的 loop 會把 slide clone 出來、切換分類也會重掛元件,同一批 URL
   會被反覆探測。用 module 層的 Map 記住結果,同一個 session 內只試一次。
   只在記憶體、不寫 storage —— CDN 補圖之後重整就會重新探測。 */
const probeCache = new Map();

/**
 * 依序探測候選清單,回傳第一個真的載得到的 URL。
 *
 * 用途是 Splide loop 的 clone 修正:先確定圖片存在再塞進 src,
 * clone 出來的節點才不會凍在一個 404 的網址上(clone 不會重跑 onError)。
 *
 * @param {string[]} candidates
 * @returns {Promise<string|null>} 全部都失敗回 null
 */
export const resolveGameImageSrc = (candidates = []) =>
  new Promise((resolve) => {
    const tryAt = (i) => {
      if (i >= candidates.length) return resolve(null);
      const url = candidates[i];

      const cached = probeCache.get(url);
      if (cached === true) return resolve(url);
      if (cached === false) return tryAt(i + 1);

      const probe = new window.Image();
      probe.onload = () => {
        probeCache.set(url, true);
        resolve(url);
      };
      probe.onerror = () => {
        probeCache.set(url, false);
        tryAt(i + 1);
      };
      probe.src = url;
    };
    tryAt(0);
  });

export default buildGameImageCandidates;
